<template>
  <div>
    <div class="px-4 pt-4">
      <BaseForm
        ref="sharePostForm"
        v-model="isSharePostFormValid"
        lazy-validation
        :disabled="isFetchingData"
      >
        <!-- Post type -->
        <section class="pb-9">
          <BaseSheet
            :outlined="!$vuetify.breakpoint.xs"
            :min-height="48"
            :rounded="true"
            :elevation="$vuetify.breakpoint.xs ? 2 : 0"
            class="px-3 d-flex justify-space-between align-center"
          >
            <!-- Post type Information -->
            <div class="d-flex align-center">
              <span v-text="postTitle" class="text--secondary pr-3"></span>
              <BaseIcon :size="16">{{ postIcon }}</BaseIcon>
            </div>
            <!-- edit post type button -->
            <BaseButton
              v-if="!post.id"
              @click="$store.dispatch('share/setPostType', null)"
              :x-small="true"
              :outlined="true"
              :disabled="isFetchingData"
              color="secondary"
            >
              modifier
            </BaseButton>
          </BaseSheet>
        </section>
        <!-- Picture Post management section -->
        <section
          v-if="
            isEditingPost ||
              post.type === 'short_post' ||
              post.type === 'picture_post'
          "
          class="mb-3"
        >
          <div
            class="d-flex align-start pb-1"
            v-bind:class="{
              'flex-wrap': post.type === 'picture_post',
              'scroll--overflow-x': post.type !== 'picture_post'
            }"
          >
            <BasePictureInput
              :disabled="this.post.pictures.length > 10"
              :maxPictureSize="10485760"
              @input="onPictureInputChange"
              @pictureOverMaxSize="
                $store.commit('mutateAlert', 'pictureOverMaxSize')
              "
              @fileIsNotAPicture="
                $store.commit('mutateAlert', 'fileIsNotAPicture')
              "
              :block="post.type === 'picture_post'"
            >
            </BasePictureInput>
            <div v-if="post.type === 'picture_post'" style="width:100%">
              <BaseInput
                :value="this.post.pictures"
                :rules="picturesInput"
              ></BaseInput>
            </div>
            <div
              class="d-flex"
              v-bind:class="{
                'scroll--overflow-x': post.type === 'picture_post',
                'ml-4': post.type === 'short_post'
              }"
            >
              <div
                v-for="(picture, index) in this.post.pictures"
                :key="index"
                class="pr-4"
              >
                <BasePictureThumbnail
                  :src="picture.id ? picture.medium : picture.localUrl"
                  :loading="!picture.id"
                  @delete="deletePostPicture(index)"
                  class="flex-shrink-0"
                  :size="post.type === 'picture_post' ? 288 : 144"
                >
                  <template v-slot:input>
                    <BaseTextField
                      v-if="post.type === 'picture_post'"
                      :value="picture.caption"
                      @input="
                        $store.dispatch('share/setPictureCaption', {
                          caption: $event,
                          index: index,
                          blur: false
                        })
                      "
                      @blur="
                        $store.dispatch('share/setPictureCaption', {
                          caption: null,
                          index: index,
                          blur: true
                        })
                      "
                      maxlength="100"
                      :clearable="true"
                      counter
                      :label="`légende photo n°${index + 1}`"
                    ></BaseTextField>
                  </template>
                </BasePictureThumbnail>
              </div>
            </div>
          </div>
          <BaseInput
            v-if="post.type === 'short_post'"
            :value="this.post.pictures"
            :rules="picturesInput"
          ></BaseInput>
        </section>
        <!-- Post title -->
        <section
          style="margin-bottom:6px"
          v-if="
            isEditingPost ||
              post.type === 'short_post' ||
              post.type === 'picture_post'
          "
        >
          <BaseTextField
            :value="post.title"
            @input="event => $store.dispatch('share/setPostTitle', event)"
            :rules="[
              v => !!v || this.$i18n.t('share.post.rules.title.required')
            ]"
            :counter="true"
            clearable
            required
            solo
            :outlined="!$vuetify.breakpoint.xs"
            :flat="!$vuetify.breakpoint.xs"
            background-color="white"
            :placeholder="$t(`share.post.title`)"
            color="primary"
            append-icon="fas fa-font"
          >
          </BaseTextField>
        </section>
        <!-- Post Location -->
        <section
          v-for="(destination, index) in post.destinations"
          :key="destination.id"
          class="d-flex align-start"
        >
          <ShareLocationAutocompleteInput
            @updateDestinationAutocomplete="
              payload => $store.dispatch('share/setPostDestination', payload)
            "
            :index="index"
            :location="destination.location"
            :ref="'destinationAutocomplete' + index"
          >
          </ShareLocationAutocompleteInput>
          <!-- Remove Location button-->
          <BaseButton
            v-if="post.destinations.length > 1"
            @click="$store.dispatch('share/deletePostDestination', index)"
            color="white"
            :height="36"
            :width="36"
            :fab="true"
            :elevation="$vuetify.breakpoint.xs ? 2 : 0"
            :depressed="!$vuetify.breakpoint.xs"
            :disabled="isFetchingData"
            class="ml-6"
            style="margin-right:6px; margin-top:6px; z-index-0"
          >
            <BaseIcon color="error" :size="12">fas fa-minus</BaseIcon>
          </BaseButton>
        </section>
        <!-- AddLocationInput -->
        <section
          v-if="post.type !== 'picture_post'"
          :style="
            `margin-bottom:${post.destinations.length < 5 ? '36px' : '6px'}`
          "
        >
          <BaseButton
            @click="$store.dispatch('share/addNewPostDestination')"
            v-if="post.destinations.length < 5"
            color="#DCE3E3"
            :depressed="!$vuetify.breakpoint.xs"
            :min-height="36"
            :disabled="isFetchingData"
            :block="true"
            :border="false"
            :height="48"
          >
            <span
              v-text="$t('share.addLocation')"
              class="mr-2 primary--text text-lowercase font-weight-medium"
            ></span>
            <BaseIcon color="primary" :size="16">
              fas fa-map-marker-alt
            </BaseIcon>
          </BaseButton>
        </section>
        <!-- Content Post Textarea -->
        <section v-if="post.type === 'short_post'" style="margin-bottom:6px">
          <BaseTextArea
            :value="post.content"
            @input="event => $store.dispatch('share/setPostContent', event)"
            required
            :rules="contentPostRules"
            :outlined="!$vuetify.breakpoint.xs"
            :flat="!$vuetify.breakpoint.xs"
            solo
            background-color="white"
            placeholder="Description"
          >
          </BaseTextArea>
        </section>
        <!-- URL Post Input -->
        <section
          v-if="/external_link|youtube/.test(post.type) && !isEditingPost"
          style="margin-bottom:6px"
        >
          <BaseTextField
            :value="post.url"
            @input="event => $store.dispatch('share/setPostUrl', event)"
            :outlined="!$vuetify.breakpoint.xs"
            :flat="!$vuetify.breakpoint.xs"
            solo
            :append-icon="postIcon"
            background-color="white"
            :placeholder="$t(`share.post.${post.type}`)"
            color="primary"
            clearable
            required
            :rules="urlRules"
            :error-messages="urlErrorMessages"
            @focus="$store.dispatch('share/setUrlErrorMessages', [])"
          >
          </BaseTextField>
        </section>

        <!-- Season Post -->
        <section style="margin-bottom:6px">
          <BaseSelect
            :items="seasons"
            item-text="nameFr"
            item-value="nameEn"
            :return-object="false"
            background-color="white"
            :value="post.season"
            @input="event => $store.dispatch('share/setPostSeason', event)"
            :placeholder="$t(`core.seasons.singular_SEASON`)"
            :clearable="true"
            :rules="seasonsRules"
          ></BaseSelect>
        </section>
        <div class="d-flex flex-wrap">
          <!-- Ambiances themes -->
          <section>
            <BaseSelector
              :name="ambiancesThemes.nameFr"
              :options="ambiancesThemes.themes"
              :value="post.ambiances"
              :multiple="true"
              buttonSize="large"
              :oneValueRequired="false"
              :styleIsActiveIfValueEgalOptions="true"
              :disabled="isFetchingData"
              class="mr-2 mb-4"
              @updateValue="
                $store.dispatch('share/setPostTheme', {
                  theme: 'ambiances',
                  value: $event
                })
              "
            >
            </BaseSelector>
          </section>
          <!-- Activities themes -->
          <section>
            <BaseSelector
              :name="activitiesThemes.nameFr"
              :options="activitiesThemes.themes"
              :value="post.activities"
              :multiple="true"
              buttonSize="large"
              :oneValueRequired="false"
              :styleIsActiveIfValueEgalOptions="true"
              :disabled="isFetchingData"
              class="mr-2 mb-4"
              @updateValue="
                $store.dispatch('share/setPostTheme', {
                  theme: 'activities',
                  value: $event
                })
              "
            >
            </BaseSelector>
          </section>
          <!-- Travellers themes -->
          <section>
            <BaseSelector
              :name="travellersThemes.nameFr"
              :options="travellersThemes.themes"
              :value="post.travellers"
              :multiple="true"
              buttonSize="large"
              :oneValueRequired="false"
              :styleIsActiveIfValueEgalOptions="true"
              :disabled="isFetchingData"
              class="mr-2 mb-4"
              @updateValue="
                $store.dispatch('share/setPostTheme', {
                  theme: 'travellers',
                  value: $event
                })
              "
            >
            </BaseSelector>
          </section>
          <!-- Transports themes -->
          <section>
            <BaseSelector
              :name="transportsThemes.nameFr"
              :options="transportsThemes.themes"
              :value="post.transports"
              :multiple="true"
              buttonSize="large"
              :oneValueRequired="false"
              :disabled="isFetchingData"
              :styleIsActiveIfValueEgalOptions="true"
              class="mr-2 mb-4"
              @updateValue="
                $store.dispatch('share/setPostTheme', {
                  theme: 'transports',
                  value: $event
                })
              "
            >
            </BaseSelector>
          </section>
        </div>
        <!-- Submit Button -->
      </BaseForm>
      <div class="bottom--sticky z-index-2">
        <BaseSheet
          width="100%"
          :height="32"
          class="background--linear"
        ></BaseSheet>
        <div class="view-background-color">
          <BaseButton
            @click="createOrEditPost()"
            :disabled="!isSharePostFormValid || isFetchingData"
            :depressed="!$vuetify.breakpoint.xs"
            large
            block
            color="primary"
          >
            <span
              v-text="isEditingPost ? $t('share.edit') : $t('app.shareAGem')"
            ></span>
          </BaseButton>
          <BaseButton
            v-if="isEditingPost"
            @click="$store.dispatch('share/cancelPostEdition')"
            :disabled="isFetchingData"
            large
            block
            text
            class="mt-4"
          >
            Annuler
          </BaseButton>
        </div>
        <BaseSheet
          width="100%"
          :height="16"
          color="grey lighten-5"
          :light="false"
        ></BaseSheet>
      </div>
    </div>
  </div>
</template>

<script>
// Libraries
import { mapState } from "vuex";
// Variables
import PostTypes from "../variables/PostTypes";
import SEASONS from "./../../core/variables/seasons.json";
// Mixins
import ErrorInputMixins from "@/services/ErrorInputMixins";
import ThemesByCategories from "../../core/mixins/ThemesByCategoryMixin";
// Components
import ShareLocationAutocompleteInput from "./ShareLocationAutocompleteInput.vue";

export default {
  components: {
    ShareLocationAutocompleteInput
  },
  //   MIXINs
  mixins: [ErrorInputMixins, ThemesByCategories],
  //   DATAs
  data() {
    return {
      isSharePostFormValid: true,
      seasonsRules: [
        v => !!v || this.$i18n.t("share.post.rules.season.required")
      ],
      websiteUrlRules: [
        v => !!v || this.$i18n.t("share.post.rules.link.required"),
        v =>
          /^http(s?):\/\//.test(v) ||
          this.$i18n.t("share.post.rules.link.valid")
      ],
      youtubeUrlRules: [
        v => !!v || this.$i18n.t("share.post.rules.youtubeUrl.required"),
        v =>
          /(^http(s)?:\/\/*)|^$/.test(v) ||
          this.$i18n.t("share.post.rules.youtubeUrl.mustStartWithProcotole"),
        v =>
          /(^http(s)?:\/\/www.youtube.com\/watch\?v=)|(^http(s)?:\/\/youtu.be\/)|^$/.test(
            v
          ) || this.$i18n.t("share.post.rules.youtubeUrl.mustBeYoutubeVideoUrl")
      ],
      contentPostRules: [
        v => !!v || this.$i18n.t("share.post.rules.content.required")
      ],
      picturesInput: [
        value =>
          !!value.length || this.$i18n.t("share.post.rules.pictures.required"),
        () =>
          this.isAllPicturesUploaded ||
          this.$i18n.t("share.post.rules.pictures.allUploaded"),
        value =>
          value.length <= 10 ||
          this.$i18n.t("share.post.rules.pictures.tooMuchPictures")
      ]
    };
  },
  computed: {
    ...mapState({
      post: state => state.share.post,
      urlErrorMessages: state => state.share.urlErrorMessages,
      isEditingPost: state => state.share.isEditingPost,
      isFetchingData: state => state.share.isFetchingData
    }),
    postIcon() {
      return PostTypes().find(type => type.name === this.post.type).icon;
    },
    postTitle() {
      return PostTypes().find(type => type.name === this.post.type).displayName;
    },
    urlRules() {
      switch (this.post.type) {
        case "external_link":
          return [
            v => !!v || this.$i18n.t("share.post.rules.link.required"),
            v =>
              /^http(s?):\/\//.test(v) ||
              this.$i18n.t("share.post.rules.link.valid")
          ];
        case "youtube":
          return [
            v => !!v || this.$i18n.t("share.post.rules.youtubeUrl.required"),
            v =>
              /(^http(s)?:\/\/*)|^$/.test(v) ||
              this.$i18n.t(
                "share.post.rules.youtubeUrl.mustStartWithProcotole"
              ),
            v =>
              /(^http(s)?:\/\/www.youtube.com\/watch\?v=)|(^http(s)?:\/\/youtu.be\/)|^$/.test(
                v
              ) ||
              this.$i18n.t("share.post.rules.youtubeUrl.mustBeYoutubeVideoUrl")
          ];
        default:
          return [];
      }
    },
    seasons() {
      return SEASONS;
    },
    isAllPicturesUploaded() {
      return this.post.pictures.every(picture => picture.id);
    },
  },
  // METHODs
  methods: {
    createOrEditPost() {
      if (this.$refs.sharePostForm.$refs.form.validate()) {
        if (this.isEditingPost) return this.$store.dispatch("share/editPost");
        return this.$store.dispatch("share/createPost");
      }
    },
    onPictureInputChange(payload) {
      payload.forEach(picture => {
        this.$store.dispatch("share/addPostPicture", picture);
      });
    },
    deletePostPicture(index) {
      this.$store.dispatch("share/deletePostPicture", index);
    }
  },
  watch: {
    isAllPicturesUploaded(value) {
      // force form to reset validation
      // input validation does not re-validate when a key in a picture is changed.
      if (value) return this.$refs.sharePostForm.$refs.form.resetValidation();
    }
  }
};
</script>
<style scoped>
.background--linear {
  background: linear-gradient(#f7f8f800, #f7f8f8);
}
</style>
